import {Injectable, Injector} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {autoSubscribe, HttpService} from 'projects/core/src/public-api';
import {tap} from 'rxjs/operators';
import {TimeCard, TimeCardType} from './time-card';
import * as moment from 'moment';
import {TimeCardsStore} from './time-cards.store';
import {PayPeriod} from './pay-period';
import {Project} from '../../project-misc/data/project/project';
import {ReportCard} from './report-data/report-card';
import {Editions} from './report-data/editions';
import {CustomCoordinates} from './custom-coordinates';
import {VacationStats} from './vacation-stats';
import {VacationDay} from 'projects/desktop/src/app/modules/timesheets/view/vacation-calendar/data/vacation-day';
import {CurrentEntry} from './current-entry';


@Injectable({
  providedIn: 'root',
})
export class TimeCardService extends HttpService {
  projectChanger = new Subject<Project>()
  vacationCounterSubj = new Subject<void>()

  openedAfterRefresh = false;

  constructor(
    protected injector: Injector,
    protected timeCardsStore: TimeCardsStore
  ) {
    super(injector);
  }

  punchIn(lat: number, lng: number, breakType?: TimeCardType, projectId?: number): Observable<TimeCard> {
    if (!breakType) {
      breakType = 'WORK'
    }
    return this.putData(`/time_card/punch-in?lat=${lat}&lon=${lng}&projectId=${projectId || 0}&type=${breakType}`, {}, TimeCard)
      .pipe(
        tap(timeCard => {
          this.timeCardsStore.updateLastTimeCard(timeCard)
          this.timeCardsStore.unshiftTimeCard(timeCard)
          this.timeCardsStore.updatePunchStatus(true)
        })
      )
  }

  punchOut(lat: number, lng: number): Observable<TimeCard> {
    return this.putData(`/time_card/punch-out?lat=${lat}&lon=${lng}`, {}, TimeCard)
      .pipe(
        tap(timeCard => {
          this.timeCardsStore.updateLastTimeCard(timeCard)
          this.timeCardsStore.updatePunchStatus(false)
        })
      )
  }

  startBreak(lat: number, lng: number, breakType?: TimeCardType): Observable<TimeCard> {
    if (!breakType) {
      breakType = 'WORK'
    }
    return this.putData(`/time_card/punch-in?lat=${lat}&lon=${lng}&type=${breakType}`, {}, TimeCard)
      .pipe(
        tap(timeCard => {
          this.timeCardsStore.updateLastTimeCard(timeCard)
          this.timeCardsStore.unshiftTimeCard(timeCard)
          this.timeCardsStore.updatePunchStatus(true)
        })
      )
  }

  finishBreak(lat: number, lng: number): Observable<TimeCard> {
    return this.putData(`/time_card/punch-out?lat=${lat}&lon=${lng}`, {}, TimeCard)
      .pipe(
        tap(timeCard => {
          this.timeCardsStore.updateLastTimeCard(timeCard)
          this.timeCardsStore.updatePunchStatus(false)
        })
        // switchMap(closedTimeCard => {
        // return this.punchIn(lat, lng, 'WORK', lastProjectId);
        // })
      )
  }

  getVacationCounter(dateFrom: string, dateTo: string): Observable<number> {
    return this.doHttpGet(`/time_card/list/vacation-requests/${dateFrom}:${dateTo}/count`);
  }

  getCards(dateFrom: string, dateTo: string): Observable<TimeCard[]> {
    return this.getData(`/time_card/my/list/${dateFrom}:${dateTo}`, TimeCard);
  }

  getClosedBySystemCards(userId: number): Observable<TimeCard[]> {
    return this.getData(`/time_card/user/${userId}/time-card/closed-by-system`, TimeCard);
  }

  getBatchCards(dateFrom: string, dateTo: string): Observable<TimeCard[]> {
    return this.getData(`/time_card/clock-in-data?dateFrom=${dateFrom}&dateTo=${dateTo}`, TimeCard);
  }

  getCardsForUser(userId: number, dateFrom: string, dateTo: string): Observable<TimeCard[]> {
    return this.getData(`/time_card/user/${userId}/list/${dateFrom}:${dateTo}`, TimeCard);
  }

  getReportCardsForUser(userId: number, dateFrom: string, dateTo: string): Observable<ReportCard> {
    return this.getData(`/time_card/user/${userId}/punch-time/report-data/${dateFrom}:${dateTo}`, ReportCard);
  }

  getInfoAboutChanges(dateFrom: string, dateTo: string): Observable<Editions[]> {
    return this.getData(`/time_card/editions/${dateFrom}:${dateTo}`, Editions);
  }

  getInfoAboutChangesForUser(userId: number, dateFrom: string, dateTo: string): Observable<ReportCard> {
    return this.getData(`/time_card/user/${userId}/punch-time/report-data/${dateFrom}:${dateTo}`, ReportCard);
  }

  getLastJob(): Observable<Project> {
    return this.getData(`/project/worked-on/last`, Project);
  }

  getTodayCards(): Observable<TimeCard[]> {
    const today = moment();

    // TEMP FOR DEV
    // if (!environment.production) {
    // 	today = moment(today).subtract(14, 'hours');
    // }

    const todayString = moment(today).format('YYYY-MM-DD')
    return this.getData(`/time_card/my/list/${todayString}:${todayString}`, TimeCard)
      .pipe(
        tap(timeCards => {
          this.timeCardsStore.updateTodayTimeCards(timeCards)
          if (timeCards && timeCards.length > 0) {
            this.timeCardsStore.updateFirstTimeCard(timeCards[timeCards.length - 1])
            this.timeCardsStore.updateLastTimeCard(timeCards[0])

            if (!timeCards[0].finishTime) {
              this.timeCardsStore.updatePunchStatus(true)
            } else {
              this.timeCardsStore.updatePunchStatus(false)
            }
          } else {
            this.timeCardsStore.updatePunchStatus(false)
          }
        }),
        autoSubscribe()
      )
  }

  getMyCards(): Observable<TimeCard[]> {
    return this.getData(`/time_card/my/opened`, TimeCard)
  }

  getCardsForAllUsers(dateFrom: string, dateTo: string): Observable<TimeCard[]> {
    return this.getData(`/time_card/list/${dateFrom}:${dateTo}`, TimeCard)

  }

  createTimeCard(timeCard: TimeCard): Observable<TimeCard> {
    return this.postData(`/time_card/create/for-user/${timeCard.user.id}`, timeCard, TimeCard)
  }

  updateTimeCard(timeCard: TimeCard): Observable<TimeCard> {
    return this.putData(`/time_card/update`, timeCard, TimeCard)
  }

  updateTimeCardByAdmin(timeCard: TimeCard): Observable<TimeCard> {
    return this.putData(`/time_card/admin-update`, timeCard, TimeCard)
  }

  getTodayTotalTime(userId: number, dateFrom: string, dateTo: string): Observable<number> {
    return this.doHttpGet<number>(`/time_card/user/${userId}/total/${dateFrom}:${dateTo}`)
      .pipe(
        tap(time => {
          this.timeCardsStore.updateTodayTime(time);
        })
      )
  }

  getWeekTotalTime(userId: number, dateFrom: string, dateTo: string): Observable<number> {
    return this.doHttpGet<number>(`/time_card/user/${userId}/total/${dateFrom}:${dateTo}`)
      .pipe(
        tap(time => {
          this.timeCardsStore.updateWeekTime(time);
        })
      )
  }

  getPPTotalTime(userId: number, dateFrom: string, dateTo: string): Observable<number> {
    return this.doHttpGet<number>(`/time_card/user/${userId}/total/${dateFrom}:${dateTo}`)
      .pipe(
        tap(time => {
          this.timeCardsStore.updatePPTime(time);
        })
      )
  }

  togglePayPeriod(userId: number, start: string, end: string): Observable<PayPeriod> {
    return this.putData(`/time_card/user/${userId}/approve/${start}:${end}`, {}, PayPeriod)
  }

  getPayPeriodsByDate(dateFrom: string, dateTo: string): Observable<PayPeriod[]> {
    return this.getData(`/time_card/pay-periods/${dateFrom}:${dateTo}`, PayPeriod)
  }

  getLastPayPeriods(): Observable<PayPeriod[]> {
    return this.getData(`/time_card/pay-periods`, PayPeriod)
  }

  getLastPayPeriodsForUser(userId: number, halfPeriods: boolean): Observable<PayPeriod[]> {
    if (halfPeriods) {
      return this.getData(`/time_card/user/${userId}/last-weeks`, PayPeriod)
    } else {
      return this.getData(`/time_card/user/${userId}/last-pay-periods`, PayPeriod)
    }
  }

  getAvailableDaysForUser(userId: number): Observable<VacationStats> {
    return this.getData(`/user/${userId}/vacation-stats`, VacationStats)
  }

  getLastVacationCardsForUser(userId: number): Observable<VacationDay[]> {
    return this.getData(`/time_card/user/${userId}/vacation-requests`, VacationDay)
  }

  getPayPeriods(count: number): Observable<PayPeriod[]> {
    return this.getData(`/time_card/time-periods/last?count=${count}`, PayPeriod)
  }

  deleteTimeCard(timeCardId: number): Observable<TimeCard> {
    return this.deleteData(`/time_card/${timeCardId}`, TimeCard)
  }

  isUserInShop(): Observable<CustomCoordinates> {
    return this.doHttpGet<CustomCoordinates>(`/user/location`);
  }

  fillHours(userId: number, start: string, end: string, hours: number): Observable<PayPeriod> {
    return this.postData(`/time_card/fill-hours?dateFrom=${start}&dateTo=${end}&userId=${userId}&hours=${hours}`, {}, PayPeriod)
  }

  getCurrentEntries(): Observable<CurrentEntry[]> {
    return this.getData(`/time_card/list/current-entries`, CurrentEntry)
  }

  updateUserVacationStats(userId: number, correctionSickDays: number, correctionVacationDays: number): Observable<VacationStats> {
    return this.putData(`/user/${userId}/vacation-stats`, {
      correctionSickDays,
      correctionVacationDays
    }, VacationStats);
  }

  exportTimesheetToCsv(userIds: string, dateFrom: string, dateTo: string): void {
    window.open(`${this.apiUrl}/time_card/csv/export?userIds=${userIds}&dateFrom=${dateFrom}&dateTo=${dateTo}`, '_blank');
  }

  importTimesheetFromCsv(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.doHttpPost<any>(`/time_card/csv/import`, formData);
  }

}
